import { saveAttachment } from './attachmentService';
import { materializeOfflineInlineImage } from './materializeOfflineInlineImage';
import {
  listOfflineInlineImages,
  removeOfflineInlineImage,
} from './offlineInlineImageQueue';
import { rewriteHtmlPreviewSrcsToPendingRefs } from './pendingInlineImagePreview';
import { setInlineImageUploadProgress } from '../utils/richContent/inlineAttachmentName';
import { isMultilineRichHtmlFieldKey } from '../utils/richContent/multilineRichHtmlFields';
import {
  collectPendingRichHtmlAttachmentIds,
  rewritePendingRichHtmlAttachmentRefs,
} from '../utils/richContent/richHtmlAttachmentRefs';

const PENDING_IMG_TAG =
  /<img\b[^>]*\bsrc=(["'])sitescope-attachment-pending:\/\/[\w-]+\1[^>]*>/gi;

export function stripPendingInlineImagesFromHtml(html: string): string {
  return html.replace(PENDING_IMG_TAG, '').trim();
}

export function rewritePendingRefsInRecord<T extends Record<string, unknown>>(
  record: T,
  idMap: Readonly<Record<string, number>>,
): T {
  if (Object.keys(idMap).length === 0) {
    return record;
  }

  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return collectPendingRichHtmlAttachmentIds(value).length > 0
        ? rewritePendingRichHtmlAttachmentRefs(value, idMap)
        : value;
    }
    if (Array.isArray(value)) {
      return value.map(walk);
    }
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, nested] of Object.entries(
        value as Record<string, unknown>,
      )) {
        out[key] = walk(nested);
      }
      return out;
    }
    return value;
  };

  return walk(record) as T;
}

export function stripPendingRefsInRecord<T extends Record<string, unknown>>(
  record: T,
): T {
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return collectPendingRichHtmlAttachmentIds(value).length > 0
        ? stripPendingInlineImagesFromHtml(value)
        : value;
    }
    if (Array.isArray(value)) {
      return value.map(walk);
    }
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, nested] of Object.entries(
        value as Record<string, unknown>,
      )) {
        out[key] = walk(nested);
      }
      return out;
    }
    return value;
  };

  return walk(record) as T;
}

function walkRecordStrings(
  value: unknown,
  mapString: (html: string) => string,
): unknown {
  if (typeof value === 'string') {
    return mapString(value);
  }
  if (Array.isArray(value)) {
    return value.map(item => walkRecordStrings(item, mapString));
  }
  if (value instanceof Set || value instanceof Map || value instanceof Date) {
    return value;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(
      value as Record<string, unknown>,
    )) {
      out[key] = walkRecordStrings(nested, mapString);
    }
    return out;
  }
  return value;
}

/** Swap in-memory preview `src`s (blob:/data:) back to pending refs before flush. */
export function rewritePreviewSrcsInRecord<T extends Record<string, unknown>>(
  record: T,
): T {
  return walkRecordStrings(record, rewriteHtmlPreviewSrcsToPendingRefs) as T;
}

export function recordHasPendingInlineImages(
  record: Record<string, unknown>,
): boolean {
  const ids = new Set<string>();
  collectPendingIdsFromValue(record, ids);
  return ids.size > 0;
}

function collectPendingIdsFromValue(value: unknown, into: Set<string>): void {
  if (typeof value === 'string') {
    for (const id of collectPendingRichHtmlAttachmentIds(value)) {
      into.add(id);
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(item => collectPendingIdsFromValue(item, into));
    return;
  }
  if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach(item =>
      collectPendingIdsFromValue(item, into),
    );
  }
}

export async function flushOfflineInlineImageQueue(options: {
  requestId: number | null;
  referencedClientIds?: ReadonlySet<string>;
  /** Upload every create-flow staged row (requestId null), even when HTML omitted the pending ref. */
  flushAllCreateStaged?: boolean;
}): Promise<Record<string, number>> {
  const queue = await listOfflineInlineImages();
  const idMap: Record<string, number> = {};

  try {
    for (const record of queue) {
      const isCreateStaged = record.requestId == null;
      const referenced =
        (options.flushAllCreateStaged === true && isCreateStaged) ||
        !options.referencedClientIds ||
        options.referencedClientIds.has(record.clientId);
      const samePersistedRequest =
        record.requestId != null &&
        options.requestId != null &&
        record.requestId === options.requestId;
      if (!referenced) {
        if (samePersistedRequest) {
          await removeOfflineInlineImage(record.clientId);
        }
        continue;
      }
      const targetRequestId = options.requestId ?? record.requestId;
      if (
        targetRequestId == null ||
        !Number.isInteger(targetRequestId) ||
        targetRequestId <= 0
      ) {
        continue;
      }
      if (
        record.requestId != null &&
        options.requestId != null &&
        record.requestId !== options.requestId
      ) {
        continue;
      }

      try {
        const file = await materializeOfflineInlineImage(record);
        const fileName = record.fileName || file.name || 'image';
        setInlineImageUploadProgress({
          fileName,
          progress: 0,
          status: 'uploading',
        });
        const response = await saveAttachment(
          file,
          targetRequestId,
          record.requestSection,
          'inline',
          undefined,
          undefined,
          undefined,
          progress => {
            setInlineImageUploadProgress({
              fileName,
              progress,
              status: 'uploading',
            });
          },
        );
        const attachmentId = response.attachment?.id;
        if (Number.isInteger(attachmentId) && attachmentId! > 0) {
          idMap[record.clientId] = attachmentId!;
          await removeOfflineInlineImage(record.clientId);
          setInlineImageUploadProgress({
            fileName,
            progress: 100,
            status: 'completed',
          });
        }
      } catch (error) {
        console.warn(
          'Failed to flush offline inline image',
          record.clientId,
          error,
        );
      }
    }
  } finally {
    setInlineImageUploadProgress(null);
  }

  return idMap;
}

export async function flushAndRewriteRichHtmlRecord<
  T extends Record<string, unknown>,
>(record: T, requestId: number | null): Promise<T> {
  const withPendingRefs = rewritePreviewSrcsInRecord(record);
  const referencedClientIds = new Set<string>();
  collectPendingIdsFromValue(withPendingRefs, referencedClientIds);
  const idMap = await flushOfflineInlineImageQueue({
    requestId,
    referencedClientIds,
  });
  return rewritePendingRefsInRecord(withPendingRefs, idMap);
}

function appendPendingImageToFieldHtml(
  html: string,
  clientId: string,
  fileName: string,
): string {
  const pendingRef = `sitescope-attachment-pending://${clientId}`;
  if (html.includes(clientId) || html.includes(pendingRef)) {
    return html;
  }
  const safeName = fileName.replace(/"/g, '&quot;');
  const tag = `<p><img src="${pendingRef}" alt="${safeName}" /></p>`;
  return html.trim() ? `${html}${tag}` : tag;
}

function resolveCreatePendingInjectFieldKey(
  record: Record<string, unknown>,
  item: { transportFieldKey?: string },
): string | null {
  const explicit = item.transportFieldKey;
  if (
    explicit &&
    isMultilineRichHtmlFieldKey(explicit) &&
    typeof record[explicit] === 'string'
  ) {
    return explicit;
  }

  const richStringKeys = Object.keys(record).filter(
    key => isMultilineRichHtmlFieldKey(key) && typeof record[key] === 'string',
  );
  if (richStringKeys.length === 1) {
    return richStringKeys[0];
  }

  return null;
}

/** Inject queue pending refs when create save raced ahead of form HTML sync. */
export function mergeCreateQueuePendingRefsIntoRecord<
  T extends Record<string, unknown>,
>(
  record: T,
  queue: ReadonlyArray<{
    clientId: string;
    requestId: number | null;
    transportFieldKey?: string;
    fileName: string;
  }>,
): T {
  const createQueue = queue.filter(item => item.requestId == null);
  if (createQueue.length === 0) {
    return record;
  }
  let next: Record<string, unknown> = { ...record };
  const serialized = () => JSON.stringify(next);

  for (const item of createQueue) {
    if (serialized().includes(item.clientId)) {
      continue;
    }
    const fieldKey = resolveCreatePendingInjectFieldKey(next, item);
    if (fieldKey) {
      next[fieldKey] = appendPendingImageToFieldHtml(
        next[fieldKey] as string,
        item.clientId,
        item.fileName,
      );
    }
  }

  return next as T;
}

export class StagedInlineImageFlushError extends Error {
  readonly failedClientIds: readonly string[];

  constructor(failedClientIds: readonly string[]) {
    super('Inline images could not be uploaded');
    this.name = 'StagedInlineImageFlushError';
    this.failedClientIds = failedClientIds;
  }
}

export type StagedInlineImageFlushResult = {
  idMap: Record<string, number>;
  uploadedCount: number;
  failedClientIds: string[];
  stagedBeforeFlush: number;
};

export async function flushAndRewriteRichHtmlRecordForCreate<
  T extends Record<string, unknown>,
>(
  record: T,
  requestId: number,
): Promise<{
  record: T;
  flush: StagedInlineImageFlushResult;
}> {
  const queue = await listOfflineInlineImages();
  const createQueue = queue.filter(item => item.requestId == null);
  let withPendingRefs = rewritePreviewSrcsInRecord(record);
  withPendingRefs = mergeCreateQueuePendingRefsIntoRecord(
    withPendingRefs,
    createQueue,
  );

  const referencedClientIds = new Set<string>();
  collectPendingIdsFromValue(withPendingRefs, referencedClientIds);
  for (const item of createQueue) {
    referencedClientIds.add(item.clientId);
  }

  const idMap = await flushOfflineInlineImageQueue({
    requestId,
    referencedClientIds,
    flushAllCreateStaged: true,
  });

  const failedClientIds = createQueue
    .filter(item => idMap[item.clientId] == null)
    .map(item => item.clientId);

  return {
    record: rewritePendingRefsInRecord(withPendingRefs, idMap),
    flush: {
      idMap,
      uploadedCount: Object.keys(idMap).length,
      failedClientIds,
      stagedBeforeFlush: createQueue.length,
    },
  };
}

export function parseNumericRequestId(
  requestId: string | number | null | undefined,
): number | null {
  if (requestId == null) {
    return null;
  }
  if (typeof requestId === 'number') {
    return Number.isInteger(requestId) && requestId > 0 ? requestId : null;
  }
  const trimmed = requestId.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}
